import type { GroupByConfig, GroupRow } from '@ayocore/platform/common/types/aggregate';
import type { RpcRepositoryClient } from '@ayocore/platform/server/rpc/types';
import type { SubscriptionStore } from './RepositoryController';

/** Строки одной раскрытой группы. */
export interface GroupChildren<T> {
  rows: T[];
  loading: boolean;
  /**
   * Сообщение, если догрузка не удалась. Пустое раскрытие без причины
   * неотличимо от «в группе нет строк», а это ложь: count у группы ненулевой.
   */
  error?: string;
  /**
   * Строк в группе больше, чем загружено (сработал предохранитель лимита).
   * Показывается пользователю: иначе он считал бы, что видит всю группу.
   */
  truncated: boolean;
}

/** Данные режима группировки. */
export interface GroupsState<T> {
  /** Группы текущей страницы (посчитаны сервером по всей выборке) */
  groups: GroupRow[];
  /** Подгруженные строки раскрытых групп, по ключу группы */
  children: Record<string, GroupChildren<T>>;
  /**
   * Номер загрузки групп, растёт на каждый успешный `loadPage`.
   *
   * Нужен вью, чтобы сбросить раскрытие: на содержимое групп полагаться нельзя —
   * после обновления с теми же итогами массив групп равен прежнему по значению,
   * а дети уже очищены. Тогда группа осталась бы раскрытой и пустой, без
   * повторной догрузки.
   */
  version: number;
}

/** Проверка «выборка уже сменилась» — ответ такого запроса писать нельзя. */
type StaleCheck = () => boolean;

/**
 * Данные режима группировки: группы страницы и лениво подгруженные строки
 * раскрытых групп.
 *
 * Отдельный стор рядом с `RepositoryController`, а не поля в нём: у групп другая
 * модель и другой жизненный цикл (страница из групп, дети догружаются по одной,
 * у каждой свои загрузка и лимит). Контроллер остаётся единственным владельцем
 * СПИСОЧНОГО состояния — фильтров, поиска, страницы, сортировки и выбора строк —
 * и делегирует сюда всё, что касается групп.
 *
 * Цепочку запроса (фильтры + поиск + сортировка) собирает контроллер и передаёт
 * сюда готовой: источник не должен знать, как устроены фильтры таблицы.
 */
export class GroupsDataSource<T extends Record<string, unknown>>
  implements SubscriptionStore<GroupsState<T>>
{
  /** Сколько строк группы тянем за раз (защита от групп в сотни строк). */
  static readonly DEFAULT_CHILDREN_LIMIT = 200;

  private static readonly EMPTY_STATE = { groups: [], children: {}, version: 0 };

  private config: GroupByConfig<T> | null = null;
  private state: GroupsState<T> = GroupsDataSource.EMPTY_STATE;
  private listeners = new Set<() => void>();

  /**
   * Ключ группы в карте детей.
   *
   * Префикс обязателен: карта — обычный объект, и значение группы вида
   * 'constructor' или 'toString' попало бы на унаследованное свойство
   * Object.prototype — чтение вернуло бы функцию вместо undefined, а обращение
   * к `.rows.length` упало бы с TypeError.
   *
   * null («пусто») — отдельная группа со своим ключом: String(null) совпал бы
   * со строкой 'null' из данных.
   */
  static childrenKey(value: GroupRow['value']): string {
    return value == null ? 'v:\u0000null' : `v:${String(value)}`;
  }

  /** Включает режим (null — выключает и очищает данные: они больше не нужны). */
  setConfig(config: GroupByConfig<T> | null): void {
    this.config = config;
    if (!config && this.state !== GroupsDataSource.EMPTY_STATE) {
      this.state = GroupsDataSource.EMPTY_STATE;
      this.emit();
    }
  }

  /** Включён ли режим группировки. */
  isEnabled(): boolean {
    return this.config !== null;
  }

  /** Поле группировки (null — режим выключен). */
  getField(): string | null {
    return this.config ? String(this.config.field) : null;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): GroupsState<T> {
    return this.state;
  }

  /** Уже загруженные (или загружаемые) строки группы. */
  getChildren(value: GroupRow['value']): GroupChildren<T> | undefined {
    return this.state.children[GroupsDataSource.childrenKey(value)];
  }

  /**
   * Загружает страницу групп и их общее число.
   *
   * @param baseQuery Цепочка с фильтрами, поиском и сортировкой (без пагинации)
   * @param opts limit/offset — по ГРУППАМ; isStale — проверка актуальности выборки
   * @returns Всего групп, либо null если ответ устарел и записан не был
   */
  async loadPage(
    baseQuery: RpcRepositoryClient<T>,
    opts: { limit: number; offset: number; isStale: StaleCheck }
  ): Promise<number | null> {
    const config = this.config;
    if (!config) return null;

    const [groups, total] = await Promise.all([
      baseQuery.aggregateBy?.({ ...config, limit: opts.limit, offset: opts.offset }) ??
        Promise.resolve([]),
      baseQuery.countGroups?.(config.field) ?? Promise.resolve(0),
    ]);

    if (opts.isStale()) return null;

    // Набор групп сменился — ранее подгруженные дети относятся к прошлой
    // выборке, держать их нельзя.
    this.state = { groups, children: {}, version: this.state.version + 1 };
    this.emit();

    return total;
  }

  /**
   * Догружает строки одной группы: та же цепочка плюс условие по ключу.
   * Повторные вызовы игнорируются — строки уже либо загружены, либо в пути.
   *
   * @param baseQuery Цепочка с фильтрами и поиском
   * @param value Значение ключа группы (null — группа «пусто»)
   * @param opts limit — максимум строк за раз; isStale — проверка актуальности
   */
  async loadChildren(
    baseQuery: RpcRepositoryClient<T>,
    value: GroupRow['value'],
    opts: { limit?: number; isStale: StaleCheck }
  ): Promise<void> {
    const config = this.config;
    if (!config) return;

    const key = GroupsDataSource.childrenKey(value);
    const existing = this.state.children[key];
    if (existing && (existing.loading || existing.rows.length > 0)) return;

    const limit = opts.limit ?? GroupsDataSource.DEFAULT_CHILDREN_LIMIT;
    this.setChildren(key, { rows: [], loading: true, truncated: false });

    try {
      // Группа «пусто» обслуживается тем же where: knex транслирует значение
      // null в `IS NULL`, отдельного whereNull для этого не требуется.
      const scoped = baseQuery.where?.(String(config.field), value) ?? baseQuery;
      // Просим на одну строку больше лимита: пришло больше — значит группа
      // показана не полностью, и об этом надо сказать пользователю.
      const rows = await (scoped.limit?.(limit + 1) ?? scoped);
      if (opts.isStale()) return;
      const truncated = rows.length > limit;
      this.setChildren(key, {
        rows: truncated ? rows.slice(0, limit) : rows,
        loading: false,
        truncated,
      });
    } catch (err) {
      if (opts.isStale()) return;
      this.setChildren(key, {
        rows: [],
        loading: false,
        truncated: false,
        error: err instanceof Error ? err.message : 'Не удалось загрузить строки группы',
      });
    }
  }

  /**
   * Выбранные строки среди подгруженных детей — для bulkActions в режиме
   * группировки. Выбор остаётся у контроллера, здесь только поиск строк по нему.
   *
   * @param selectedIds Бизнес-id выбранных строк (источник правды — контроллер)
   * @param getRowKey Ключ строки. Обязателен: у таблиц бывает составной ключ
   *   (`getRowId` таблицы), и `row.id` там не совпадает с тем, под чем выбор
   *   лежит в контроллере — bulkActions получили бы пустой список.
   */
  getSelectedRows(selectedIds: readonly string[], getRowKey: (row: T) => string): T[] {
    const selected = new Set(selectedIds);
    const rows: T[] = [];
    for (const children of Object.values(this.state.children)) {
      for (const row of children.rows) {
        if (selected.has(getRowKey(row))) rows.push(row);
      }
    }
    return rows;
  }

  /**
   * Сбрасывает подгруженных детей и бампает `version`.
   *
   * Нужен ветке ошибки обновления: `requestSeq` там уже вырос, поэтому ответы
   * догрузок отбрасываются как устаревшие, а их плейсхолдеры `loading: true`
   * остались бы навсегда — повторный клик по такой группе игнорируется
   * (загрузка «уже идёт»). Бамп версии заодно свернёт раскрытие во вью.
   */
  resetChildren(): void {
    this.state = {
      groups: this.state.groups,
      children: {},
      version: this.state.version + 1,
    };
    this.emit();
  }

  /** Точечное обновление карты детей (новая ссылка — иначе селекторы не заметят). */
  private setChildren(key: string, children: GroupChildren<T>): void {
    this.state = {
      groups: this.state.groups,
      children: { ...this.state.children, [key]: children },
      version: this.state.version,
    };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
